import React, { memo, useMemo } from 'react';
import { Card, CardBody, CardTitle } from 'reactstrap';
import { TableColumn } from '@modules/common/TableColumn';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { FormCheckbox, FormInput } from '@core/components/form';
import { Formatter } from '@core/utils/formatter';
import { PermissionService } from '@business/services/permission_service';
import { OrderFormType } from './TimeMaterialForm';
import { TmFeeForm } from '@modules/proposal/ProposalBuilderPage/Components/ProposalProductsOverlay';
import { R } from '@core/utils/r';
import { ProposalService } from '@business/services';
import { useCompany } from '@core/utils/hooks/misc';

export const ProductsTableHeader = memo(() => {
  return (
    <Card style={{ marginBottom: 10, backgroundColor: 'transparent', boxShadow: 'inherit' }}>
      <CardBody style={{ padding: '0.655rem' }}>
        <div className="proposal-table-header">
          <TableColumn flex="initial" width={50}></TableColumn>
          <TableColumn flex={2}>Name</TableColumn>
          <TableColumn flex={2}>Total</TableColumn>
          <TableColumn flex={1}>Taxable</TableColumn>
          <TableColumn maxWidth={100}>Percentage</TableColumn>
        </div>
      </CardBody>
    </Card>
  );
});

export interface TimeMaterialFeeLineProps {
  form: OrderFormType;
  ProposalItemsTotal: number;
}

export const TimeMaterialFeeLine = memo((props: TimeMaterialFeeLineProps) => {
  const { form, ProposalItemsTotal } = props;
  const feeItems = useObservable(form.controls.fees.items$, []);
  const isUserAdmin = useMemo(() => PermissionService.isAdminUser(), []);
  const isProposalFeeEnabled = useMemo(() => PermissionService.isProposalFeeEnabled(), []);

  return (
    <>
      {feeItems.length > 0 && (
        <>
          <CardTitle className="d-flex align-items-center justify-content-between">
            <div className="row py-2 ml-1">
              <span className="title mr-3 mt-2">Fees</span>
            </div>
          </CardTitle>
          <ProductsTableHeader />
          {feeItems.map((item, index) => (
            <FeeLine
              key={index}
              index={index}
              item={item}
              proposalTotal={ProposalItemsTotal}
              isUserAdmin={isUserAdmin}
              isProposalFeeEnabled={isProposalFeeEnabled}
              form={form}
            />
          ))}
        </>
      )}
    </>
  );
});

export interface FeeLineProps {
  form: OrderFormType;
  item: TmFeeForm;
  proposalTotal: number;
  isUserAdmin: boolean | null;
  index: number;
  isProposalFeeEnabled: boolean;
}
export const FeeLine = memo((props: FeeLineProps) => {
  const { item, proposalTotal, isUserAdmin, form, index, isProposalFeeEnabled } = props;
  const itemValue = useNonNilObservable(item.value$);
  const formValue = useNonNilObservable(form.value$);

  // const feeTotal = useMemo(() => {
  //   const fTotal = (proposalTotal / 100) * itemValue.percent;
  //   form.controls.fees.items[index].controls.feeTotal.setValue(fTotal);
  //   return fTotal;
  // }, [form.controls.fees.items, index, itemValue.percent, proposalTotal]);

  const feeTotal = useMemo(() => {
    const feeType = form.controls.fees.items[index].controls.feeType.value;
    const tot = formValue.lineItems.reduce((total, line) => total + R.toNumber(ProposalService.getSubTotalAddProd(line, false, feeType)), 0);
    const fTotal = (tot / 100) * itemValue.percent;
    form.controls.fees.items[index].controls.feeTotal.setValue(fTotal);
    return fTotal;
  }, [form.controls.fees.items, formValue.lineItems, index, itemValue.percent]);
  const company = useCompany();

  const isPercentageDisabled = useMemo(() => {
    if (isUserAdmin) {
      return false;
    } else {
      return !isProposalFeeEnabled;
    }
  }, [isUserAdmin, isProposalFeeEnabled]);

  return (
    <div className="d-flex proposal-price-item">
      <TableColumn width={50}>
        <FormCheckbox style={{ lineHeight: 1 }} noPadding control={item.controls.isChecked} />
      </TableColumn>
      <TableColumn flex={2}>{itemValue.name}</TableColumn>
      <TableColumn flex={2}>{Formatter.formatCurrency(feeTotal, company.currencyCode)}</TableColumn>
      <TableColumn flex={1}>
        <FormCheckbox control={item.controls.taxable} />
      </TableColumn>
      <TableColumn maxWidth={100}>
        <FormInput style={{ lineHeight: 1 }} noPadding control={item.controls.percent} disabled={isPercentageDisabled} type="number" />
      </TableColumn>
    </div>
  );
});
